import * as React from 'react'

import { deriveContentState } from './deriveContentState'
import { useGlobalSearchItemActions } from './useGlobalSearchItemActions'
import { useGlobalSearchKeyboardNavigation } from './useGlobalSearchKeyboardNavigation'
import { useGlobalSearchQueryState } from './useGlobalSearchQueryState'
import { useGlobalSearchTabState } from './useGlobalSearchTabState'
import { useGlobalSearchNavigation } from '../../../../hooks/useGlobalSearchNavigation'
import { type GlobalSearchRecentItem } from '../../../../value-objects/GlobalSearchRecentItem'
import { type GlobalSearchResultItem } from '../../../../value-objects/GlobalSearchResultItem'
import { type ContentStateEnum } from '../../enums/ContentStateEnum'
import {
  type GlobalSearchTab,
  type GlobalSearchTabTypeEnum,
} from '../../enums/GlobalSearchTabTypeEnum'
import { useMutateAddGlobalSearchRecentItem } from '../../models/useMutateAddGlobalSearchRecentItem'
import { useQueryGlobalSearchRecentItems } from '../../models/useQueryGlobalSearchRecentItems'
import { useQueryGlobalSearchResults } from '../../models/useQueryGlobalSearchResults'

interface IUseGetGlobalSearchResultsPresenterResult {
  commands: {
    changeQuery: (value: string) => void
    openHighlightedOrSubmit: () => void
    openRecentItem: (item: GlobalSearchRecentItem) => void
    openResultItem: (item: GlobalSearchResultItem) => void
    selectTab: (type: GlobalSearchTabTypeEnum) => void
    navigateDown: () => void
    navigateUp: () => void
  }
  data: {
    query: string
    recentItems: GlobalSearchRecentItem[]
    resultItems: GlobalSearchResultItem[]
    tabs: Nullable<GlobalSearchTab[]>
    inputRef: React.RefObject<HTMLInputElement>
    highlightedIndex: Nullable<number>
    activeDescendantId: string | undefined
    isExpanded: boolean
  }
  states: {
    showEnterHint: boolean
    contentState: Nullable<ContentStateEnum>
    selectedTab: GlobalSearchTabTypeEnum
    showTruncatedNotice: boolean
  }
}

export const useGetGlobalSearchResultsPresenter = (
  onClose: () => void
): IUseGetGlobalSearchResultsPresenterResult => {
  const { query, submittedQuery, setSubmittedQuery, setQuery, inputRef } =
    useGlobalSearchQueryState()

  const { navigate } = useGlobalSearchNavigation()
  const { recentItems } = useQueryGlobalSearchRecentItems()
  const { addRecentItem } = useMutateAddGlobalSearchRecentItem()
  const {
    data: searchResult,
    isLoading: isSearchLoading,
    isError: isSearchError,
  } = useQueryGlobalSearchResults(submittedQuery)

  const {
    selectedTab,
    selectTab: setTab,
    filteredResultItems,
    tabs,
    resetTab,
  } = useGlobalSearchTabState(searchResult)

  const { openRecentItem, openResultItem } = useGlobalSearchItemActions({
    onClose,
    navigate,
    addRecentItem,
  })

  const submitQuery = React.useCallback(() => {
    setSubmittedQuery((prev) => (prev === query ? prev : query))
    resetTab()
  }, [query, setSubmittedQuery, resetTab])

  const contentState: Nullable<ContentStateEnum> = deriveContentState({
    hasSubmittedQuery: submittedQuery !== '',
    hasRecentItems: recentItems.length > 0,
    isSearchLoading,
    hasResultItems: (searchResult?.items.length ?? 0) > 0,
    isSearchSuccess:
      !isSearchLoading && !isSearchError && searchResult !== undefined,
    isUnsupportedQuery: searchResult?.isUnsupportedQuery ?? false,
    isSearchError,
  })

  const {
    highlightedIndex,
    isExpanded,
    activeDescendantId,
    navigateDown,
    navigateUp,
    openHighlightedItem,
    resetHighlight,
  } = useGlobalSearchKeyboardNavigation({
    query,
    recentItems,
    filteredResultItems,
    contentState,
    openRecentItem,
    openResultItem,
  })

  const changeQuery = React.useCallback(
    (value: string) => {
      resetHighlight()
      setQuery(value)
    },
    [resetHighlight, setQuery]
  )

  const selectTab = React.useCallback(
    (type: GlobalSearchTabTypeEnum) => {
      resetHighlight()
      setTab(type)
    },
    [resetHighlight, setTab]
  )

  const openHighlightedOrSubmit = React.useCallback(() => {
    if (!openHighlightedItem()) submitQuery()
  }, [openHighlightedItem, submitQuery])

  const showEnterHint =
    query !== submittedQuery && query.length > 0 && highlightedIndex === null

  const showTruncatedNotice = searchResult?.isTruncated ?? false

  return {
    commands: {
      changeQuery,
      openHighlightedOrSubmit,
      openRecentItem,
      openResultItem,
      selectTab,
      navigateDown,
      navigateUp,
    },
    data: {
      query,
      recentItems,
      resultItems: filteredResultItems,
      tabs,
      inputRef,
      highlightedIndex,
      activeDescendantId,
      isExpanded,
    },
    states: {
      showEnterHint,
      contentState,
      selectedTab,
      showTruncatedNotice,
    },
  }
}
